import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import walletService from '../services/walletService';

const WalletContext = createContext();

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

export const WalletProvider = ({ children }) => {
  const { currentUser, token } = useAuth();
  const [walletData, setWalletData] = useState({
    balances: {
      USD: 0,
      INR: 0,
      LOCAL: 0,
      practice: 0
    },
    primaryBalance: 0,
    primaryCurrency: 'USD',
    currencySymbol: '$',
    countryCode: 'US',
    countryName: 'United States',
    localCurrencyCode: 'LOCAL',
    localCurrencySymbol: '$'
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Set token on walletService when token changes
  useEffect(() => {
    if (token) {
      walletService.setToken(token);
    }
  }, [token]);

  // Initial wallet data fetch when user logs in
  useEffect(() => {
    if (currentUser && token) {
      console.log('🔄 WalletContext: Fetching wallet data for user', currentUser.username);
      fetchWalletData();
    } else {
      resetWallet();
    }
  }, [currentUser, token]);

  // Reset wallet data
  const resetWallet = () => {
    setWalletData({
      balances: { USD: 0, INR: 0, LOCAL: 0, practice: 0 },
      primaryBalance: 0,
      primaryCurrency: 'USD',
      currencySymbol: '$',
      countryCode: 'US',
      countryName: 'United States',
      localCurrencyCode: 'LOCAL',
      localCurrencySymbol: '$'
    });
    setTransactions([]);
    setError(null);
    setLastUpdated(null);
  };

  // Main wallet data fetch function
  const fetchWalletData = useCallback(async () => {
    if (!token || !currentUser) {
      console.log('⚠️ WalletContext: No token or user, skipping wallet fetch');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('💰 WalletContext: Fetching wallet data from server...');
      const response = await walletService.getWalletData();
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      console.log('✅ WalletContext: Received wallet data:', response);
      
      // Extract data from response
      const balances = response.balances || {
        USD: 0,
        INR: 0,
        LOCAL: 0,
        practice: 0
      };
      
      // Get country data from response or currentUser
      const countryCode = response.country?.code || currentUser?.country || 'US';
      const countryName = response.country?.name || currentUser?.countryName || 'United States';
      const localCurrencyCode = response.country?.currencyCode || 'USD';
      const localCurrencySymbol = response.country?.currencySymbol || '$';
      
      // Determine which currency to show as LOCAL based on country
      let displayLocalCurrency = 'LOCAL';

      if (countryCode === 'BJ') {
        displayLocalCurrency = 'CFA';
      } else if (countryCode === 'LR') {
        displayLocalCurrency = 'LRD';
      } else if (countryCode === 'US' || countryCode === 'IN') {
        displayLocalCurrency = null;
      }

      // Expose all four real wallets (USD, INR, LRD, LOCAL) so a player can pay with,
      // and receive winnings in, whichever wallet they choose.
      const updatedBalances = {
        USD: balances.USD || 0,
        INR: balances.INR || 0,
        LRD: balances.LRD || 0,
        LOCAL: balances.LOCAL || 0,
        BONUS: balances.BONUS || 0,
        LOYALTY: balances.LOYALTY || 0,
        practice: balances.practice || 0
      };

      // Update wallet data
      const newWalletData = {
        balances: updatedBalances,
        primaryBalance: balances.USD || 0, // Default to USD as primary
        primaryCurrency: 'USD',
        currencySymbol: '$',
        countryCode,
        countryName,
        localCurrencyCode: displayLocalCurrency || localCurrencyCode,
        localCurrencySymbol: response.country?.currencySymbol || 
                           (displayLocalCurrency === 'CFA' ? 'CFA' : 
                            displayLocalCurrency === 'LRD' ? 'L$' : '$'),
        countryInfo: response.country || {}
      };
      
      console.log('📊 WalletContext: Setting wallet data:', newWalletData);
      setWalletData(newWalletData);
      
      setLastUpdated(new Date().toISOString());
      return response;
      
    } catch (error) {
      console.error('❌ WalletContext: Failed to fetch wallet data:', error);
      setError(`Unable to load wallet: ${error.message}`);
      
      // Set default data based on currentUser if available
      const defaultCountryCode = currentUser?.country || 'US';
      const defaultCountryName = currentUser?.countryName || 'United States';
      
      setWalletData({
        balances: { USD: 0, INR: 0, LOCAL: 0, practice: 0 },
        primaryBalance: 0,
        primaryCurrency: 'USD',
        currencySymbol: '$',
        countryCode: defaultCountryCode,
        countryName: defaultCountryName,
        localCurrencyCode: defaultCountryCode === 'BJ' ? 'CFA' : 
                          defaultCountryCode === 'LR' ? 'LRD' : 'LOCAL',
        localCurrencySymbol: defaultCountryCode === 'BJ' ? 'CFA' : 
                            defaultCountryCode === 'LR' ? 'L$' : '$'
      });
      
      throw error;
    } finally {
      setLoading(false);
    }
  }, [token, currentUser]);

  // Helper function to get currency symbol
  const getCurrencySymbol = (currencyCode) => {
    if (currencyCode === 'LOCAL') {
      return walletData.localCurrencySymbol;
    }
    
    const symbols = {
      'USD': '$',
      'INR': '₹',
      'LRD': 'L$',
      'CFA': 'CFA',
      'XOF': 'CFA',
      'EUR': '€',
      'GBP': '£'
    };
    
    return symbols[currencyCode?.toUpperCase()] || currencyCode;
  };

  // Get formatted primary balance
  const getFormattedBalance = useCallback(() => {
    const { primaryBalance, primaryCurrency } = walletData;
    const symbol = getCurrencySymbol(primaryCurrency);
    
    return walletService.formatCurrency(
      primaryBalance, 
      primaryCurrency,
      symbol
    );
  }, [walletData]);

  // Get formatted balance for specific currency
  const getFormattedBalanceForCurrency = useCallback((currency) => {
    const balance = (currency === 'LOCAL' && walletData.localCurrencyCode === 'LRD') ? (walletData.balances.LRD || 0) : (walletData.balances[currency] || 0);
    
    // For LOCAL currency, use the user's local currency
    if (currency === 'LOCAL') {
      return walletService.formatCurrency(
        balance,
        walletData.localCurrencyCode,
        walletData.localCurrencySymbol
      );
    }
    
    return walletService.formatCurrency(balance, currency);
  }, [walletData]);

  // Get user's primary balance info
  const getPrimaryBalanceInfo = useCallback(() => {
    return {
      amount: walletData.primaryBalance,
      currency: walletData.primaryCurrency,
      symbol: walletData.currencySymbol,
      country: walletData.countryName,
      countryCode: walletData.countryCode,
      localCurrencyCode: walletData.localCurrencyCode,
      localCurrencySymbol: walletData.localCurrencySymbol,
      formatted: getFormattedBalance()
    };
  }, [walletData, getFormattedBalance]);

  // Get formatted LOCAL balance
  const getLocalBalanceInfo = useCallback(() => {
    const localBalance = walletData.localCurrencyCode === 'LRD' ? (walletData.balances.LRD || 0) : (walletData.balances.LOCAL || 0);
    
    return {
      amount: localBalance,
      currency: walletData.localCurrencyCode,
      symbol: walletData.localCurrencySymbol,
      formatted: walletService.formatCurrency(
        localBalance,
        walletData.localCurrencyCode,
        walletData.localCurrencySymbol
      )
    };
  }, [walletData]);

  // Check if user can afford amount in their primary currency
  const canAfford = useCallback((amount) => {
    const canAfford = parseFloat(walletData.primaryBalance) >= parseFloat(amount);
    return canAfford;
  }, [walletData]);

  // Check if user can afford in specific currency
  const canAffordInCurrency = useCallback((amount, currency) => {
    const balance = (currency === 'LOCAL' && walletData.localCurrencyCode === 'LRD') ? (walletData.balances.LRD || 0) : (walletData.balances[currency] || 0);
    const canAfford = parseFloat(balance) >= parseFloat(amount);
    return canAfford;
  }, [walletData]);

  // Add funds
  const addFunds = async (amount, currency = 'USD', paymentMethod = 'stripe') => {
    setLoading(true);
    setError(null);
    
        try {
      console.log(`➕ Adding ${amount} ${currency} to wallet`);
      
      // Update local state immediately
      const updatedBalances = { ...walletData.balances };
      updatedBalances[currency] = Number(Math.round(((parseFloat(updatedBalances[currency]) || 0) + parseFloat(amount)) * 100) / 100);
      
      // Update primary balance if this is USD
      let updatedPrimaryBalance = walletData.primaryBalance;
      if (currency === 'USD') {
        updatedPrimaryBalance = Number(Math.round(((parseFloat(walletData.primaryBalance) || 0) + parseFloat(amount)) * 100) / 100);
      }
      
      const updatedData = {
        ...walletData,
        balances: updatedBalances,
        primaryBalance: updatedPrimaryBalance
      };
      
      console.log('📈 Updated wallet data:', updatedData);
      setWalletData(updatedData);
      
      // Try to save to backend
      try {
        await walletService.addFunds(amount, currency, paymentMethod);
      } catch (backendError) {
        console.warn('⚠️ Could not save to backend:', backendError.message);
      }
      
      setLastUpdated(new Date().toISOString());
      return { success: true, newBalance: updatedBalances[currency] };
      
    } catch (error) {
      console.error('❌ Error adding funds:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Deduct for game entry
  const deductForGameEntry = async (amount, currency = 'USD') => {
    if (!canAffordInCurrency(amount, currency)) {
      throw new Error(`Insufficient ${currency} balance. You need ${amount} ${currency}`);
    }
    
    setLoading(true);
    
        try {
      console.log(`➖ Deducting ${amount} ${currency} for game entry`);
      
      // Update local state immediately
      const updatedBalances = { ...walletData.balances };
      updatedBalances[currency] = Number(Math.round(((parseFloat(updatedBalances[currency]) || 0) - parseFloat(amount)) * 100) / 100);
      
      // Update primary balance if this is USD
      let updatedPrimaryBalance = walletData.primaryBalance;
      if (currency === 'USD') {
        updatedPrimaryBalance = Number(Math.round(((parseFloat(walletData.primaryBalance) || 0) - parseFloat(amount)) * 100) / 100);
      }
      
      const updatedData = {
        ...walletData,
        balances: updatedBalances,
        primaryBalance: updatedPrimaryBalance
      };
      
      console.log('📉 Updated wallet data:', updatedData);
      setWalletData(updatedData);
      
      // Try to save to backend
      try {
        await walletService.deductFunds(amount, currency, 'game_entry');
      } catch (backendError) {
        console.warn('⚠️ Could not save deduction to backend:', backendError.message);
      }
      
      setLastUpdated(new Date().toISOString());
      return { success: true, newBalance: updatedBalances[currency] };
      
    } catch (error) {
      console.error('❌ Error deducting for game entry:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Add game winnings
  const addGameWinnings = async (amount, currency = 'USD') => {
    setLoading(true);
    
        try {
      console.log(`🎉 Adding game winnings: ${amount} ${currency}`);
      
      // Update local state immediately
      const updatedBalances = { ...walletData.balances };
      updatedBalances[currency] = Number(Math.round(((parseFloat(updatedBalances[currency]) || 0) + parseFloat(amount)) * 100) / 100);
      
      // Update primary balance if this is USD
      let updatedPrimaryBalance = walletData.primaryBalance;
      if (currency === 'USD') {
        updatedPrimaryBalance = Number(Math.round(((parseFloat(walletData.primaryBalance) || 0) + parseFloat(amount)) * 100) / 100);
      }
      
      const updatedData = {
        ...walletData,
        balances: updatedBalances,
        primaryBalance: updatedPrimaryBalance
      };
      
      console.log('💰 Updated wallet data:', updatedData);
      setWalletData(updatedData);
      
      // Try to save to backend
      try {
        await walletService.addFunds(amount, currency, 'game_winnings');
      } catch (backendError) {
        console.warn('⚠️ Could not save winnings to backend:', backendError.message);
      }
      
      setLastUpdated(new Date().toISOString());
      return { success: true, newBalance: updatedBalances[currency] };
      
    } catch (error) {
      console.error('❌ Error adding game winnings:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Get total balance across all currencies
  const getTotalBalance = useCallback(() => {
    const { balances } = walletData;
    return Object.values(balances).reduce((sum, balance) => sum + parseFloat(balance), 0);
  }, [walletData]);

  // Manually refresh wallet
  const refreshWallet = useCallback(() => {
    console.log('🔄 Manually refreshing wallet data');
    return fetchWalletData();
  }, [fetchWalletData]);

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Get all currency balances for display
  const getAllBalances = useCallback(() => {
    const result = [];
    
    // Always show USD and INR
    result.push({
      currency: 'USD',
      balance: walletData.balances.USD || 0,
      formatted: walletService.formatCurrency(walletData.balances.USD || 0, 'USD', '$')
    });
    
    result.push({
      currency: 'INR',
      balance: walletData.balances.INR || 0,
      formatted: walletService.formatCurrency(walletData.balances.INR || 0, 'INR', '₹')
    });
    
    // Show LOCAL only if user has a local currency (not US or India)
    if (walletData.localCurrencyCode && walletData.localCurrencyCode !== 'USD' && walletData.localCurrencyCode !== 'INR') {
      const localBalance = walletData.localCurrencyCode === 'LRD' ? (walletData.balances.LRD || 0) : (walletData.balances.LOCAL || 0);
      result.push({
        currency: walletData.localCurrencyCode,
        balance: localBalance,
        formatted: walletService.formatCurrency(
          localBalance,
          walletData.localCurrencyCode,
          walletData.localCurrencySymbol
        )
      });
    }
    
    // Always show practice chips
    result.push({
      currency: 'Practice',
      balance: walletData.balances.practice || 0,
      formatted: walletService.formatCurrency(walletData.balances.practice || 0, '', '')
    });
    
    return result;
  }, [walletData]);

  const value = {
    // State
    walletData,
    walletBalances: walletData.balances,
    
    // Formatted values
    getFormattedBalance,
    getFormattedBalanceForCurrency,
    getPrimaryBalanceInfo,
    getLocalBalanceInfo,
    getAllBalances,
    getTotalBalance,
    
    // Balance actions
    addFunds,
    deductForGameEntry,
    addGameWinnings,
    refreshWallet,
    forceUpdate: refreshWallet,
    
    // Currency functions
    canAfford,
    canAffordInCurrency,
    getCurrencySymbol,
    
    // Other state
    transactions,
    loading,
    error,
    lastUpdated,
    
    // Utility functions
    clearError,
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};